import { useEffect, useState, useMemo } from "react";
import { useQuery, useUpdateQuery } from "./useQuery";

export type UpdateFilterFunction<T> = <S extends keyof T>(
  name: S,
  newValue: T[S]
) => void;

export type UpdateMultipleFilterFunction<T> = (updates: Partial<T>) => void;

export const useFilters = <T extends {}>({
  initialValue,
  useQueryParams = false,
}: {
  initialValue: T;
  useQueryParams?: boolean;
}) => {
  const [value, setValue] = useState(initialValue);
  const query = useQuery();
  const { update, updateMultiple } = useUpdateQuery();

  const initialValueJSON = useMemo(
    () => JSON.stringify(initialValue),
    [initialValue]
  );

  useEffect(() => {
    if (useQueryParams) {
      updateMultiple(
        Object.entries(initialValue)
          .map(([key, value]) => ({
            query: key,
            value: query.get(key) || value,
          }))
          .filter((entry) => entry.value)
      );
    }
  }, [initialValueJSON, useQueryParams]);

  const updateFilter: UpdateFilterFunction<T> = <S extends keyof T>(
    name: S,
    newValue: T[S]
  ) => {
    if (useQueryParams) {
      const updateValue = Array.isArray(newValue)
        ? newValue.join(",")
        : newValue;
      update({ query: name, value: updateValue || null });
    }

    setValue((value) => ({ ...value, [name]: newValue }));
  };

  const updateMultipleFilters: UpdateMultipleFilterFunction<T> = (updates) => {
    if (useQueryParams) {
      updateMultiple(
        Object.entries(updates).map(([key, val]) => {
          const updateValue = Array.isArray(val) ? val.join(",") : val;
          return {
            query: key,
            value: updateValue || null,
          };
        })
      );
    }

    setValue((prev) => ({ ...prev, ...updates }));
  };

  const updateFilterCallback =
    (name: Parameters<typeof updateFilter>[0]) =>
    (newValues: Parameters<typeof updateFilter>[1]) => {
      updateFilter(name, newValues);
    };

  const queryStateValues = useMemo(
    () =>
      Object.keys(initialValue).reduce((acc, key) => {
        const queryValue = query.get(key as string);
        const basevalue = queryValue
          ? Array.isArray(initialValue[key as keyof T])
            ? queryValue.split(",")
            : queryValue
          : value[key as keyof T];

        acc[key as keyof T] = basevalue as T[keyof T];
        return acc;
      }, {} as T),
    [initialValue, query, value]
  );

  return {
    updateFilter,
    updateMultipleFilters,
    values: useQueryParams ? queryStateValues : value,
    getUpdateFilter: updateFilterCallback,
  };
};
